import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react';
import type { DataRow, ColumnDefinition, TabDefinition, FilterDefinition, Insight, QuickFilterDefinition } from './types';
export type { DataRow, ColumnDefinition, TabDefinition, FilterDefinition, Insight, QuickFilterDefinition } from './types';
import { TableHeader, StatusTabs, TablePagination, FilterBar, StackedRows, FilterChips } from './components';
import { renderCell, ICON_SIZE } from './lib/renderCell';
import { useCardTable } from './hooks/useCardTable';
import { useBreakpoint } from '@/hooks/use-breakpoint';

// --- CONSTANTS ---
export const DEFAULT_ROWS_PER_PAGE = 20;

// --- TYPES & CONSTANTS MOVED ---
// Types moved to './types' and cell rendering + related constants moved to './lib/renderCell'

// --- SUB-COMPONENTS MOVED ---
// InsightsPanel, FilterBar, TableHeader, StatusTabs, and TablePagination moved to './components/*'

// Cell rendering moved to './lib/renderCell'


// --- MAIN COMPONENT ---

export interface CardTableProps {
  title?: string;
  data: DataRow[];
  columns: ColumnDefinition[];
  tabs?: TabDefinition[];
  quickFilters?: QuickFilterDefinition[];
  selectAllScope?: 'page' | 'filtered' | 'all';
  activeTab?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  currentPage?: number;
  itemsPerPage?: number;
  rowsPerPageOptions?: number[];
  searchPlaceholder?: string;
  showHeader?: boolean;
  showHeaderActions?: boolean;
  /** Optional custom actions rendered on the far right of the header */
  headerRightActions?: React.ReactNode;
  showTabs?: boolean;
  showPagination?: boolean;
  allowRowExpansion?: boolean;
  showRowMenu?: boolean;
  showFilterBar?: boolean;
  activeFilterCount?: number;
  filters?: FilterDefinition[];
  responsiveMode?: 'scroll' | 'stack';
  /** Optional render prop to fully customize the Tabs UI while using internal tab state */
  renderTabs?: (ctx: {
    tabs: TabDefinition[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
    counts: Record<string, number>;
  }) => React.ReactNode;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onTabChange?: (tabId: string) => void;
  onPageChange?: (page: number) => void;
  onSearch?: (query: string) => void;
  onRowAction?: (action: string, rowId: string) => void;
  onFilterChange?: (filterId: string, value: any) => void;
  onFilterToggle?: () => void;
  onColumnsToggle?: () => void;
  onItemsPerPageChange?: (rows: number) => void;
  renderExpandedRow?: (row: DataRow) => React.ReactNode;
  className?: string;
}

export const CardTable = React.forwardRef<HTMLDivElement, CardTableProps>((
  { 
    data,
    columns,
    title = 'Records',
    tabs = [],
    quickFilters = [],
    selectAllScope = 'page',
    activeTab: controlledActiveTab,
    sortColumn: controlledSortColumn,
    sortDirection: controlledSortDirection,
    currentPage: controlledCurrentPage,
    itemsPerPage = DEFAULT_ROWS_PER_PAGE,
    rowsPerPageOptions = [10, 20, 50],
    searchPlaceholder = 'Search...',
    showHeader = true,
    showHeaderActions = true,
    headerRightActions,
    showTabs = true,
    showPagination = true,
    allowRowExpansion = true,
    showRowMenu = true,
    showFilterBar: controlledShowFilterBar = false,
    activeFilterCount = 0,
    filters = [],
    responsiveMode = 'scroll',
    renderTabs,
    onSort,
    onTabChange,
    onPageChange,
    onSearch,
    onRowAction,
    onFilterChange = () => {},
    onFilterToggle: controlledOnFilterToggle,
    onColumnsToggle = () => {},
    onItemsPerPageChange,
    renderExpandedRow,
    className
  }, ref) => {

  const {
    activeTab,
    sortColumn,
    sortDirection,
    currentPage,
    internalSearch,
    filterValues,
    selectedRows,
    expandedRows,
    filterBarVisible,
    filteredData,
    paginatedData,
    totalPages,
    pageSize,
    handleSort,
    handleTabChange,
    handlePageChange,
    handleSearch,
    handleFilterChange,
    activeQuickFilterIds,
    handleQuickFilterToggle,
    handleSelectAll,
    handleRowSelect,
    handleRowExpand,
    handleFilterToggle,
    handleItemsPerPageChange,
    visibleColumns,
    visibleColumnKeys,
    handleToggleColumn,
    filtersApplied,
    selectAllTargetIds,
    tabCounts,
  } = useCardTable({
    data,
    columns,
    tabs,
    filters,
    quickFilters,
    selectAllScope,
    controlledActiveTab,
    controlledSortColumn,
    controlledSortDirection,
    controlledCurrentPage,
    controlledShowFilterBar,
    itemsPerPage,
    onSort,
    onTabChange,
    onPageChange,
    onSearch,
    onFilterChange,
    onFilterToggle: controlledOnFilterToggle,
    onItemsPerPageChange,
  });

  const { isMobile } = useBreakpoint();
  const shouldStack = responsiveMode === 'stack' && isMobile;

  const tabsWithCounts = React.useMemo(() => {
    return tabs.map(t => ({ ...t, count: tabCounts?.[t.id] ?? t.count }));
  }, [tabs, tabCounts]);

  return (
    <div ref={ref} className={cn('bg-sky-50 rounded-xl shadow-sm border border-gray-200', className)}>
      {showHeader && 
        <div className="px-6 py-4 border-b border-gray-200">
          <TableHeader 
            title={title} 
            searchValue={internalSearch} 
            searchPlaceholder={searchPlaceholder} 
            onSearch={handleSearch}
            onFilterToggle={handleFilterToggle}
            activeFilterCount={filtersApplied}
            columns={columns}
            visibleColumnKeys={visibleColumnKeys}
            onToggleColumn={handleToggleColumn}
            rightActions={headerRightActions}
          />
        </div>
      }
      {/* Quick filter chips below header */}
      {quickFilters.length > 0 && (
        <FilterChips
          filters={quickFilters}
          activeIds={activeQuickFilterIds}
          onToggle={handleQuickFilterToggle}
        />
      )}
      {filterBarVisible && <FilterBar filters={filters} onFilterChange={handleFilterChange} />}
      {showTabs && (
        <div className="px-4 pb-2">
          {renderTabs ? (
            renderTabs({
              tabs: tabsWithCounts,
              activeTab,
              onTabChange: handleTabChange,
              counts: tabCounts,
            })
          ) : (
            <StatusTabs
              tabs={tabsWithCounts}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          )}
        </div>
      )}
      
      {shouldStack ? (
        <StackedRows
          rows={paginatedData}
          columns={visibleColumns}
          selectedRows={selectedRows}
          onRowSelect={handleRowSelect}
          allowRowExpansion={allowRowExpansion}
          expandedRows={expandedRows}
          onRowExpand={handleRowExpand}
          renderExpandedRow={renderExpandedRow}
          onRowAction={onRowAction}
          showRowMenu={showRowMenu}
        />
      ) : (
        <div className="p-4">
          <div className="bg-white rounded-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="pl-6 pr-3 py-3 text-left w-10">
                  <Checkbox
                    onCheckedChange={handleSelectAll}
                    checked={(() => {
                      const selectedInScope = selectedRows.filter(id => selectAllTargetIds.includes(id)).length;
                      return selectAllTargetIds.length > 0 && selectedInScope === selectAllTargetIds.length
                        ? true
                        : selectedInScope > 0
                          ? 'indeterminate'
                          : false;
                    })()}
                  />
                </th>
                {visibleColumns.map(col => (
                  <th key={col.key} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {col.sortable ? (
                      <button className="flex items-center gap-1" onClick={() => handleSort(col.key)}>
                        {col.label}
                        {sortColumn === col.key && (sortDirection === 'asc' ? <ChevronUp className={ICON_SIZE} /> : <ChevronDown className={ICON_SIZE} />)}
                      </button>
                    ) : col.label}
                  </th>
                ))}
                {(allowRowExpansion || showRowMenu) && <th className="px-3 pr-6 py-3 text-right"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedData.map(row => (
                <React.Fragment key={row.id}>
                  <tr className="hover:bg-gray-50/50 transition-colors">
                    <td className="pl-6 pr-3 py-4 whitespace-nowrap"><Checkbox checked={selectedRows.includes(row.id)} onCheckedChange={() => handleRowSelect(row.id)} /></td>
                    {visibleColumns.map(col => (
                      <td key={col.key} className="px-3 py-4 text-sm text-gray-900">{renderCell(row, col)}</td>
                    ))}
                    {(allowRowExpansion || showRowMenu) && (
                      <td className="px-3 pr-6 py-4 text-right">
                        {allowRowExpansion && (
                          <Button variant="ghost" size="icon" onClick={() => handleRowExpand(row.id)}>
                            <ChevronDown className={cn('transition-transform', expandedRows.includes(row.id) && 'rotate-180')} />
                          </Button>
                        )}
                        {showRowMenu && (
                          <Button variant="ghost" size="icon" onClick={() => onRowAction && onRowAction('menu', row.id)}>
                            <MoreHorizontal className={ICON_SIZE} />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                  {allowRowExpansion && expandedRows.includes(row.id) && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1 + (allowRowExpansion || showRowMenu ? 1 : 0)} className="p-4 bg-gray-50">
                        {renderExpandedRow ? renderExpandedRow(row) : <p>Expanded content for {row.id}</p>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
              </table>
            </div>
            {showPagination && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                selectedCount={selectedRows.length}
                totalCount={filteredData.length}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleItemsPerPageChange}
                rowsPerPageOptions={rowsPerPageOptions}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
});

CardTable.displayName = 'CardTable';


